import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Send, Headphones, Minimize2, MoreVertical, Smile, Paperclip,
  UserCheck, Bot, Loader2, CheckCircle2, Image as ImageIcon, FileText, Download, Sparkles,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import EMOJI_CATEGORIES from '../data/emojiPicker';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'vt_chat_session_id';
const AVATAR = 'https://static.prod-images.emergentagent.com/jobs/bf071946-25ec-4e0e-8f4f-8dcd8991b8f1/images/0ff454843366071130f08653041113b2dd8ff2dd1e7c09a6fad9da0fb144dac5.png';

/* ─────────────────────────  Helpers  ───────────────────────── */
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const QUICK_PROMPTS_EN = [
  'How do I open an account?',
  'What is the minimum deposit?',
  'How does Copy Trading work?',
  'Explain leverage & margin',
];
const QUICK_PROMPTS_AR = [
  'كيف أفتح حساباً؟',
  'ما هو الحد الأدنى للإيداع؟',
  'كيف يعمل نسخ التداول؟',
  'اشرح لي الرافعة المالية',
];

/* ─────────────────────  Message Bubble  ───────────────────── */
const MessageBubble = ({ msg, isRTL }) => {
  const isUser   = msg.sender === 'user';
  const isAgent  = msg.sender === 'agent';
  const isSystem = msg.sender === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <div className="px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11.5px] font-semibold">
          {msg.content}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'} max-w-full`}
    >
      {!isUser && (
        <div className={`w-7 h-7 rounded-full shrink-0 grid place-items-center text-[10px] font-bold ${
          isAgent ? 'bg-emerald-500 text-white' : 'bg-flame-100 text-flame-700'
        }`}>
          {isAgent ? (msg.sender_name?.[0] || 'A').toUpperCase() : <Bot className="w-3.5 h-3.5" />}
        </div>
      )}
      <div className={`flex flex-col max-w-[78%] ${isUser ? 'items-end' : 'items-start'}`}>
        {!isUser && (
          <div className="text-[10px] text-ink-500 px-1 mb-0.5 font-semibold">
            {msg.sender_name || (isAgent ? 'Agent' : 'Varo AI')}
          </div>
        )}
        <div className={`px-3.5 py-2.5 rounded-2xl text-[14px] leading-[1.5] whitespace-pre-wrap break-words ${
          isUser
            ? 'bg-flame-500 text-white rounded-br-sm'
            : isAgent
              ? 'bg-emerald-50 text-ink-900 border border-emerald-100 rounded-bl-sm'
              : 'bg-ink-50 text-ink-900 rounded-bl-sm'
        }`}>
          {msg.content}
          {(msg.attachments || []).map((att, i) => (
            <div key={i} className="mt-2">
              {(att.mime || '').startsWith('image/') ? (
                <a href={att.data_url} target="_blank" rel="noreferrer" className="block">
                  <img src={att.data_url} alt={att.name} className="rounded-lg max-h-44 object-cover border border-white/20" />
                </a>
              ) : (
                <a href={att.data_url} download={att.name}
                   className={`mt-1 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold ${isUser ? 'bg-white/20 text-white' : 'bg-white text-ink-700 border border-ink-200'}`}>
                  <FileText className="w-3.5 h-3.5" /> {att.name} <Download className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
        <div className={`text-[10px] text-ink-400 mt-0.5 px-1 ${isUser ? 'text-end' : 'text-start'}`}>
          {fmtTime(msg.created_at)}
        </div>
      </div>
    </motion.div>
  );
};

/* ──────────────────────  Emoji Picker  ────────────────────── */
const EmojiPicker = ({ onPick, isRTL }) => {
  const [cat, setCat] = useState('smileys');
  const current = EMOJI_CATEGORIES.find((c) => c.id === cat) || EMOJI_CATEGORIES[0];
  return (
    <div className="bg-white border border-ink-200 rounded-2xl shadow-2xl w-[280px] overflow-hidden" data-testid="chat-emoji-picker">
      <div className="flex gap-1 border-b border-ink-100 p-1.5 overflow-x-auto">
        {EMOJI_CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)}
            className={`shrink-0 w-8 h-8 grid place-items-center rounded-lg text-[16px] transition ${
              cat === c.id ? 'bg-flame-100' : 'hover:bg-ink-50'
            }`} title={c.name}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 max-h-[240px] overflow-y-auto p-2">
        {current.emojis.map((e, i) => (
          <button key={`${cat}-${i}`} onClick={() => onPick(e)}
            className="w-8 h-8 grid place-items-center text-[18px] hover:bg-flame-50 rounded-md transition">
            {e}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ════════════════════════ Main Widget ════════════════════════ */
const AIChatWidget = () => {
  const { isRTL } = useLanguage();
  const t = (ar, en) => (isRTL ? ar : en);

  const [open, setOpen]               = useState(false);
  const [popup, setPopup]             = useState(false);
  const [sessionId, setSessionId]     = useState(() => localStorage.getItem(SESSION_KEY));
  const [session, setSession]         = useState(null);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [sending, setSending]         = useState(false);
  const [showEmoji, setShowEmoji]     = useState(false);
  const [attachments, setAttachments] = useState([]);   // pending to send
  const [showMenu, setShowMenu]       = useState(false);
  const [requestingAgent, setReqAgent]= useState(false);

  const fileInputRef = useRef(null);
  const scrollRef    = useRef(null);
  const pollRef      = useRef(null);
  const lastTsRef    = useRef(null);

  // ─── Auto-popup teaser every 12s when closed
  useEffect(() => {
    if (open) { setPopup(false); return; }
    const trigger = () => { setPopup(true); setTimeout(() => setPopup(false), 4000); };
    const t1 = setTimeout(trigger, 4000);
    const t2 = setInterval(trigger, 22000);
    return () => { clearTimeout(t1); clearInterval(t2); };
  }, [open]);

  // ─── Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // ─── Boot session: create if none exists when opening
  const startSession = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/api/support-chat/session`, {
        guest_name: localStorage.getItem('vt_guest_name') || 'Guest',
        initial_lang: isRTL ? 'ar' : 'en',
      });
      const sid = res.data.session_id;
      localStorage.setItem(SESSION_KEY, sid);
      setSessionId(sid);
      return sid;
    } catch (e) { return null; }
  }, [isRTL]);

  const fetchSession = useCallback(async (sid) => {
    try {
      const r = await axios.get(`${API}/api/support-chat/session/${sid}`);
      setSession(r.data);
    } catch { /* may 404 if expired */ }
  }, []);

  const fetchMessages = useCallback(async (sid, sinceTs = null) => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/api/support-chat/session/${sid}/messages`,
        sinceTs ? { params: { since: sinceTs } } : undefined);
      const incoming = r.data || [];
      if (sinceTs) {
        if (incoming.length > 0) {
          setMessages((prev) => [...prev, ...incoming]);
          lastTsRef.current = incoming[incoming.length - 1].created_at;
        }
      } else {
        setMessages(incoming);
        if (incoming.length) lastTsRef.current = incoming[incoming.length - 1].created_at;
      }
    } catch (e) {
      // Session might have expired — clear and start fresh
      if (e.response?.status === 404) {
        localStorage.removeItem(SESSION_KEY);
        setSessionId(null); setMessages([]);
      }
    }
  }, []);

  // ─── On open
  useEffect(() => {
    if (!open) return;
    (async () => {
      let sid = sessionId;
      if (!sid) sid = await startSession();
      if (sid) { await fetchSession(sid); await fetchMessages(sid); }
    })();
  }, [open]); // eslint-disable-line

  // ─── Polling when human-agent mode active
  useEffect(() => {
    if (!open || !sessionId) return;
    const isHumanMode = session && (session.status === 'awaiting_agent' || session.status === 'active_agent');
    if (!isHumanMode) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }

    pollRef.current = setInterval(async () => {
      await fetchMessages(sessionId, lastTsRef.current);
      await fetchSession(sessionId);
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, sessionId, session?.status, fetchMessages, fetchSession]);

  // ─── Send message
  const send = async (textOverride = null) => {
    const content = (textOverride ?? input).trim();
    if (!content && attachments.length === 0) return;
    if (sending) return;

    let sid = sessionId;
    if (!sid) sid = await startSession();
    if (!sid) return;

    setSending(true);
    setShowEmoji(false);

    // Optimistic user bubble
    const optimistic = {
      id: `tmp-${Date.now()}`, sender: 'user', content,
      sender_name: 'You', attachments, created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setInput('');
    const toSend = attachments;
    setAttachments([]);

    try {
      const r = await axios.post(`${API}/api/support-chat/session/${sid}/message`, {
        content, attachments: toSend,
      });
      lastTsRef.current = optimistic.created_at;
      // Refresh full history to get the AI replies
      await fetchMessages(sid);
      await fetchSession(sid);
    } catch (e) { /* keep optimistic */ }
    finally { setSending(false); }
  };

  // ─── Request agent
  const requestAgent = async () => {
    if (!sessionId) return;
    setReqAgent(true);
    try {
      await axios.post(`${API}/api/support-chat/session/${sessionId}/request-agent`);
      await fetchSession(sessionId);
      await fetchMessages(sessionId);
    } catch { /* noop */ }
    finally { setReqAgent(false); setShowMenu(false); }
  };

  // ─── File upload
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/support-chat/session/${sessionId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [...prev, r.data]);
    } catch (err) {
      alert(t('فشل الرفع. تأكد أن الملف ≤ 5MB.', 'Upload failed. File must be ≤ 5MB.'));
    } finally { e.target.value = ''; }
  };

  const onEmojiPick = (e) => { setInput((v) => v + e); };
  const quick = isRTL ? QUICK_PROMPTS_AR : QUICK_PROMPTS_EN;
  const status = session?.status || 'ai';
  const statusBadge = {
    ai:             { ar: 'Varo AI متصل',           en: 'Varo AI online',         color: 'bg-flame-500',  Icon: Sparkles },
    awaiting_agent: { ar: 'بانتظار وكيل بشري…',     en: 'Connecting to agent…',   color: 'bg-amber-500',  Icon: Loader2 },
    active_agent:   { ar: 'متصل بوكيل دعم',         en: 'Connected to agent',     color: 'bg-emerald-500',Icon: UserCheck },
    closed:         { ar: 'انتهت المحادثة',         en: 'Conversation closed',    color: 'bg-ink-500',    Icon: CheckCircle2 },
  }[status] || statusBadge?.ai;

  return (
    <>
      {/* ───────── Floating teaser bubble ───────── */}
      <AnimatePresence>
        {!open && popup && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            className={`fixed bottom-24 ${isRTL ? 'right-4 md:right-6' : 'left-4 md:left-6'} z-[59] max-w-[230px] bg-white border border-ink-200 shadow-xl rounded-2xl rounded-bl-sm px-3.5 py-2.5`}
            data-no-scroll-lock="true">
            <p className="text-[12.5px] font-semibold text-ink-900 leading-snug">
              {t('مرحباً 👋 كيف يمكنني مساعدتك؟', 'Hi 👋 How can I help you?')}
            </p>
            <div className="text-[10.5px] text-ink-500">{t('VAROTRADES AI', 'VAROTRADES AI')}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───────── Floating Trigger Button ───────── */}
      {!open && (
        <motion.button
          onClick={() => setOpen(true)}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.92 }}
          className={`fixed bottom-5 ${isRTL ? 'right-4 md:right-6' : 'left-4 md:left-6'} z-[58] w-14 h-14 rounded-full overflow-hidden shadow-[0_8px_28px_rgba(216,83,8,0.45)] ring-4 ring-white/95 transition-all`}
          data-no-scroll-lock="true"
          data-testid="chat-open-btn"
          aria-label="Open chat">
          <div className="absolute inset-0 bg-gradient-to-br from-flame-400 to-flame-600" />
          <img src={AVATAR} alt="Varo" className="relative w-full h-full object-cover" />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse" />
        </motion.button>
      )}

      {/* ───────── Chat Panel ───────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className={`fixed bottom-5 ${isRTL ? 'right-4 md:right-6' : 'left-4 md:left-6'} z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[640px] max-h-[calc(100vh-2.5rem)] bg-white rounded-3xl shadow-[0_24px_70px_rgba(20,15,10,0.25)] border border-ink-100 flex flex-col overflow-hidden`}
            dir={isRTL ? 'rtl' : 'ltr'}
            data-no-scroll-lock="true"
            data-testid="chat-panel">
            {/* Header */}
            <div className="relative px-4 py-3 bg-gradient-to-br from-flame-500 to-flame-700 flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/40 shrink-0">
                <img src={AVATAR} alt="" className="w-full h-full object-cover" />
                <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-white ${statusBadge.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-[14px] truncate">VAROTRADES Support</div>
                <div className="flex items-center gap-1 text-white/85 text-[11px] font-medium">
                  <statusBadge.Icon className={`w-3 h-3 ${status === 'awaiting_agent' ? 'animate-spin' : ''}`} />
                  {t(statusBadge.ar, statusBadge.en)}
                </div>
              </div>
              <div className="relative">
                <button onClick={() => setShowMenu((v) => !v)}
                  className="w-8 h-8 rounded-lg hover:bg-white/15 text-white grid place-items-center"
                  data-testid="chat-menu-btn">
                  <MoreVertical className="w-4 h-4" />
                </button>
                {showMenu && (
                  <div className={`absolute top-full mt-1 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-ink-200 shadow-xl rounded-xl py-1.5 min-w-[180px] z-10`}>
                    {status === 'ai' && (
                      <button onClick={requestAgent} disabled={requestingAgent}
                        className="w-full text-start px-3.5 py-2 hover:bg-flame-50 text-[12.5px] font-semibold text-ink-800 flex items-center gap-2 disabled:opacity-50"
                        data-testid="chat-request-agent-btn">
                        <Headphones className="w-3.5 h-3.5 text-flame-600" />
                        {t('التحدث مع وكيل بشري', 'Talk to a human agent')}
                      </button>
                    )}
                    <button onClick={() => {
                      localStorage.removeItem(SESSION_KEY);
                      setSessionId(null); setMessages([]); setSession(null); setShowMenu(false);
                      setTimeout(() => startSession(), 100);
                    }} className="w-full text-start px-3.5 py-2 hover:bg-ink-50 text-[12.5px] font-semibold text-ink-700 flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      {t('بدء محادثة جديدة', 'Start a new chat')}
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-white/15 text-white grid place-items-center"
                data-testid="chat-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#FAFAF8]">
              {messages.length === 0 && (
                <div className="text-center text-ink-400 text-[12.5px] py-8">
                  {t('جارٍ تحميل المحادثة…', 'Loading conversation…')}
                </div>
              )}
              {messages.map((m, idx) => <MessageBubble key={m.id || idx} msg={m} isRTL={isRTL} />)}
              {sending && (
                <div className="flex gap-2 items-center">
                  <div className="w-7 h-7 rounded-full bg-flame-100 text-flame-700 grid place-items-center">
                    <Bot className="w-3.5 h-3.5" />
                  </div>
                  <div className="px-3.5 py-2.5 rounded-2xl bg-ink-50 flex gap-1">
                    {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce" style={{animationDelay: `${i*0.12}s`}}/>)}
                  </div>
                </div>
              )}

              {messages.length <= 1 && !sending && (
                <div className="pt-2">
                  <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-400 mb-2 px-1">
                    {t('أسئلة شائعة', 'Quick prompts')}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {quick.map((q, i) => (
                      <button key={i} onClick={() => send(q)}
                        className="px-2.5 py-1.5 rounded-full bg-white border border-flame-200 text-flame-700 text-[11.5px] font-semibold hover:bg-flame-50 transition"
                        data-testid={`chat-quick-${i}`}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pending attachments preview */}
            {attachments.length > 0 && (
              <div className="px-3 pt-2 pb-1 flex gap-2 overflow-x-auto border-t border-ink-100 bg-white">
                {attachments.map((a, i) => (
                  <div key={i} className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-ink-100 border border-ink-200">
                    {(a.mime || '').startsWith('image/') ?
                      <img src={a.data_url} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full grid place-items-center text-ink-500"><FileText className="w-5 h-5"/></div>}
                    <button onClick={() => setAttachments((p) => p.filter((_, x) => x !== i))}
                      className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-ink-900 text-white grid place-items-center">
                      <X className="w-2.5 h-2.5"/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Emoji picker float */}
            {showEmoji && (
              <div className="absolute bottom-[68px] left-2 z-30">
                <EmojiPicker onPick={onEmojiPick} isRTL={isRTL} />
              </div>
            )}

            {/* Input row */}
            {status !== 'closed' ? (
              <div className="px-3 py-2.5 border-t border-ink-100 bg-white flex items-end gap-1.5">
                <button onClick={() => setShowEmoji((v) => !v)}
                  className={`w-9 h-9 grid place-items-center rounded-lg ${showEmoji ? 'bg-flame-50 text-flame-600' : 'text-ink-500 hover:bg-ink-50'}`}
                  data-testid="chat-emoji-btn"
                  aria-label="Insert emoji">
                  <Smile className="w-[18px] h-[18px]" />
                </button>
                <button onClick={() => fileInputRef.current?.click()}
                  className="w-9 h-9 grid place-items-center rounded-lg text-ink-500 hover:bg-ink-50"
                  data-testid="chat-attach-btn"
                  aria-label="Attach file">
                  <Paperclip className="w-[18px] h-[18px]" />
                </button>
                <input ref={fileInputRef} type="file" hidden onChange={onFile} accept="image/*,application/pdf,text/*" />
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t('اكتب رسالتك…', 'Type your message…')}
                  rows={1}
                  className="flex-1 min-w-0 resize-none bg-ink-50 rounded-2xl px-3.5 py-2.5 text-[13.5px] text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-flame-500/30 max-h-24"
                  data-testid="chat-input"
                />
                <button onClick={() => send()} disabled={sending || (!input.trim() && attachments.length === 0)}
                  className="w-10 h-10 grid place-items-center rounded-xl bg-flame-500 hover:bg-flame-600 active:bg-flame-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="chat-send-btn"
                  aria-label="Send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 border-t border-ink-100 bg-ink-50 text-center text-[12px] text-ink-600">
                {t('انتهت هذه المحادثة. ابدأ محادثة جديدة من القائمة بالأعلى.',
                   'This conversation is closed. Start a new chat from the menu above.')}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
